import React, { useCallback, useMemo, useEffect } from "react";
import {
  AdminStateProvider,
  useAdminState,
} from "../hooks/admin/useAdminStateManager";
import { useAdminData } from "../hooks/admin/useAdminData";
import { usePermissions } from "../hooks/admin/permissions/usePermissions";
import TabNavigation from "./TabNavigation";
import AdminToolbar from "./AdminToolbar";
import AssignmentGenericManagement from "./AssignmentGenericManagement/AssignmentGenericManagement";
import PermissionsManagement from "./PermissionsManagement/PermissionsManagement";
import LoadingState from "./LoadingState";
import { ErrorState } from "./ErrorState";
import UnsavedChangesModal from "./UnsavedChangesModal";
import {
  groupConfig,
  userConfig,
} from "./AssignmentGenericManagement/managementConfigs";

// Inner component that uses the context
const AdminUtilsContent = () => {
  const {
    activeTab,
    currentSelection,
    currentHasChanges,
    selectedGroupForPermissions,
    isPermissionsTabVisible,
    permissionsCache,
    isSaving,
    isAdding, // Coming from the hook
    isDeleting, // Coming from the hook
    hasChanges,
    pendingTabId,

    updateHasChanges,
    switchTab,
    deleteItem,
    addItem,
    showPermissionsForGroup,
    requestGroupChange,
    setPermissionsCache,
    executeSave,
    setActiveTab,
    setPendingTabId,
    selectItem,
  } = useAdminState();

  const {
    groups,
    users,
    userDetails,
    loading,
    error,
    clearError,
    updateItemLocally,
    addItemLocally,
    deleteItem: deleteItemFromData,
    setGroups,
    setUsers,
  } = useAdminData();

  const permissionsHookResult = usePermissions(
    selectedGroupForPermissions,
    permissionsCache,
    setPermissionsCache
  );

  // Sync permissions hasChanges with the main state
  useEffect(() => {
    if (activeTab === "permissions") {
      updateHasChanges("permissions", permissionsHookResult.hasChanges);
    }
  }, [activeTab, permissionsHookResult.hasChanges, updateHasChanges]);

  // Build tabs array
  const baseTabs = [
    {
      id: "groups",
      label: "Groups",
      config: groupConfig,
      component: "assignments",
    },
    {
      id: "users",
      label: "Users",
      config: userConfig,
      component: "assignments",
    },
  ];

  const tabs = useMemo(() => {
    if (isPermissionsTabVisible) {
      return [
        ...baseTabs,
        {
          id: "permissions",
          label: `Permissions ${selectedGroupForPermissions?.name || ""}`,
          component: "permissions",
        },
      ];
    }
    return baseTabs;
  }, [isPermissionsTabVisible, selectedGroupForPermissions?.name]);

  // Direct toolbar save handler
  const handleToolbarSave = useCallback(async () => {
    if (activeTab === "permissions") {
      await permissionsHookResult.handleSave("toolbar");
    } else {
      // Directly execute the registered save handler for groups/users
      await executeSave(activeTab);
    }
  }, [activeTab, permissionsHookResult, executeSave]);

  // Delete handler - loading state managed in the hook
  const handleToolbarDelete = useCallback(async () => {
    const items = activeTab === "groups" ? groups : users;
    const setItems = activeTab === "groups" ? setGroups : setUsers;
    await deleteItem(activeTab, currentSelection, items, setItems);
  }, [
    activeTab,
    currentSelection,
    groups,
    users,
    deleteItem,
    setGroups,
    setUsers,
  ]);

  // Add handler - loading state managed in the hook
  const handleToolbarAdd = useCallback(async () => {
    const items = activeTab === "groups" ? groups : users;
    const newItem = await addItem(activeTab, items);

    if (newItem) {
      addItemLocally(activeTab === "groups" ? "group" : "user", newItem);
      selectItem(activeTab, newItem);
    }
  }, [activeTab, groups, users, addItem, addItemLocally, selectItem]);

  // Handle tab changes with unsaved changes check
  const handleTabChange = useCallback(
    (tabId) => {
      // Check for unsaved changes in permissions
      if (
        activeTab === "permissions" &&
        tabId !== "permissions" &&
        permissionsHookResult.hasChanges
      ) {
        setPendingTabId(tabId);
        return; // Modal will be shown below
      }

      switchTab(tabId);
    },
    [activeTab, permissionsHookResult.hasChanges, switchTab, setPendingTabId]
  );

  // Modal handlers for permissions unsaved changes
  const handleModalSave = useCallback(async () => {
    await permissionsHookResult.handleSave("modal");
    switchTab(pendingTabId);
    setPendingTabId(null);
  }, [permissionsHookResult, pendingTabId, switchTab, setPendingTabId]);

  const handleModalDiscard = useCallback(() => {
    permissionsHookResult.handleCancel();
    switchTab(pendingTabId);
    setPendingTabId(null);
  }, [permissionsHookResult, pendingTabId, switchTab, setPendingTabId]);

  const handleModalCancel = useCallback(() => {
    setPendingTabId(null);
  }, [setPendingTabId]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} onClearError={clearError} />;

  return (
    <div className="flex flex-col bg-white workAreaPage">
      <AdminToolbar
        activeTab={activeTab}
        onSave={handleToolbarSave}
        onDelete={handleToolbarDelete}
        onAdd={handleToolbarAdd}
        hasChanges={currentHasChanges}
        isSaving={
          activeTab === "permissions"
            ? permissionsHookResult.savingFromToolbar
            : isSaving
        }
        isBottomSaving={
          activeTab === "permissions" ? permissionsHookResult.saving : false
        }
        isAdding={isAdding}
        isDeleting={isDeleting}
        selectedItem={currentSelection}
      />

      <TabNavigation
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        disabledTabs={
          permissionsHookResult.saving || permissionsHookResult.loading
            ? ["groups", "users"]
            : []
        }
      />

      <div className="flex-1 min-h-0">
        {activeTab === "permissions" ? (
          <PermissionsManagement
            selectedGroup={selectedGroupForPermissions}
            permissionsHookResult={permissionsHookResult}
            isToolbarSaving={permissionsHookResult.savingFromToolbar}
          />
        ) : (
          <AssignmentGenericManagement
            key={activeTab}
            config={activeTab === "groups" ? groupConfig : userConfig}
            items={activeTab === "groups" ? groups : users}
            assignableItems={activeTab === "groups" ? users : groups}
            userDetails={userDetails}
            updateItemLocally={updateItemLocally}
            onShowPermissions={
              activeTab === "groups" ? showPermissionsForGroup : undefined
            }
            onRequestGroupChange={
              activeTab === "groups" ? requestGroupChange : undefined
            }
            selectedGroupForPermissions={selectedGroupForPermissions}
            isPermissionsTabVisible={isPermissionsTabVisible}
            tab={activeTab}
          />
        )}
      </div>

      <UnsavedChangesModal
        show={!!pendingTabId}
        onHide={handleModalCancel}
        onLeftAction={handleModalSave}
        onRightAction={handleModalDiscard}
        message={`Do you want to save changes for permissions of ${selectedGroupForPermissions?.name}?`}
        leftLabel="Save"
        rightLabel="Discard"
      />
    </div>
  );
};

// Main component with provider
const AdminUtils = ({ apiEndPoint }) => {
  return (
    <AdminStateProvider>
      <AdminUtilsContent />
    </AdminStateProvider>
  );
};

export default AdminUtils;
